import { useState } from 'react';
import {
  useSupervisedCrew, useForemanClockIn, useForemanClockOut, useForceCloseShift,
} from '../../hooks/useForemanCrew.js';
import { useJobs } from '../../hooks/useJobs.js';
import { useError } from '../shared/ErrorBanner.jsx';
import { getPosition } from '../../lib/geo.js';
import { formatDuration } from '../../lib/format.js';

// Foreman/admin supervisor surface. Per-user buttons (no batch) so each
// action is intentional and auditable. JHA-not-submitted disables the
// clock-in button with a clear tooltip — the API returns
// ApiError.reason === 'jha_required_for_target' if it gets through anyway
// (admin can flip app_settings.payroll.foreman_skips_jha to skip the gate).
export default function CrewRosterPanel() {
  const { data: crew = [], isLoading } = useSupervisedCrew();
  const { data: jobs = [] } = useJobs('active');
  const [pickedJobByUser, setPickedJobByUser] = useState({});

  if (isLoading) {
    return (
      <div className="panel">
        <h3 className="panel-title"><span><i className="ph ph-users-three" /> Crew</span></h3>
        <div className="loading">Loading crew…</div>
      </div>
    );
  }

  if (crew.length === 0) {
    return (
      <div className="panel">
        <h3 className="panel-title"><span><i className="ph ph-users-three" /> Crew</span></h3>
        <div className="loading">No supervised crew assigned.</div>
      </div>
    );
  }

  return (
    <div className="panel">
      <h3 className="panel-title">
        <span><i className="ph ph-users-three" /> Crew ({crew.length})</span>
      </h3>
      <ul className="crew-roster">
        {crew.map(member => (
          <CrewRow
            key={member.user_id}
            member={member}
            jobs={jobs}
            pickedJobId={pickedJobByUser[member.user_id] || ''}
            onPickJob={(jobId) => setPickedJobByUser(prev => ({ ...prev, [member.user_id]: jobId }))}
          />
        ))}
      </ul>
    </div>
  );
}

function CrewRow({ member, jobs, pickedJobId, onPickJob }) {
  const clockIn = useForemanClockIn();
  const clockOut = useForemanClockOut();
  const forceClose = useForceCloseShift();
  const { show } = useError();
  const [forceCloseOpen, setForceCloseOpen] = useState(false);

  const busy = clockIn.isPending || clockOut.isPending || forceClose.isPending;
  const isOpen = !!member.open_shift;
  const jhaOk = member.today_jha_submitted;
  // Stuck = open shift older than 12h (rough client-side threshold matching
  // the server's default; server still enforces app_settings.payroll.stuck_shift_hours).
  const stuck = isOpen && (Date.now() - member.open_shift.clock_in_at) >= 12 * 3_600_000;

  const onIn = async () => {
    try {
      const job = jobs.find(j => j.id === pickedJobId);
      const needGeo = !!(job && job.site_lat != null && job.site_lng != null);
      const pos = await getPosition({ required: needGeo });
      await clockIn.mutateAsync({
        target_user_id: member.user_id,
        job_id: pickedJobId || null,
        lat: pos.lat, lng: pos.lng, accuracy_m: pos.accuracy_m,
      });
    } catch (err) {
      if (err.status === 412 && err.reason === 'jha_required_for_target') {
        show(`${err.body?.missing_user_name || member.user_name} needs to submit today's JHA first.`);
      } else if (err.status === 422 && err.body?.distance_m != null) {
        show(`${member.user_name} is ${err.body.distance_m}m from the site (allowed: ${err.body.allowed_m}m).`);
      } else {
        show(err.message);
      }
    }
  };

  const onOut = async () => {
    try {
      const pos = await getPosition({ required: false });
      await clockOut.mutateAsync({
        target_user_id: member.user_id,
        lat: pos.lat, lng: pos.lng,
      });
    } catch (err) {
      show(err.message);
    }
  };

  const onForceClose = async (reason) => {
    try {
      await forceClose.mutateAsync({ target_user_id: member.user_id, reason });
      setForceCloseOpen(false);
    } catch (err) {
      show(err.message);
    }
  };

  return (
    <li className={'crew-row' + (isOpen ? ' on' : '')}>
      <div className="crew-row-main">
        <div className="crew-row-name">
          <span className={'dot' + (isOpen ? ' on' : '')} />
          {member.user_name}
        </div>
        <div className="crew-row-meta">
          {isOpen ? (
            <>
              {member.open_shift.job_name || 'No job'}
              {' · '}
              {formatDuration(Date.now() - member.open_shift.clock_in_at)}
            </>
          ) : jhaOk ? (
            <span style={{ color: 'var(--warm-gray)' }}>Off · JHA ✓</span>
          ) : (
            <span style={{ color: 'var(--warning, var(--orange))' }}>Off · JHA missing</span>
          )}
        </div>
      </div>
      <div className="crew-row-actions">
        {!isOpen && (
          <select
            className="field-select"
            value={pickedJobId}
            onChange={e => onPickJob(e.target.value)}
            disabled={busy}
            aria-label={`Job for ${member.user_name}`}
          >
            <option value="">— no job —</option>
            {jobs.map(j => (
              <option key={j.id} value={j.id}>
                {j.name}{j.client ? ` · ${j.client}` : ''}{j.site_lat != null ? ' · GEO' : ''}
              </option>
            ))}
          </select>
        )}
        {!isOpen ? (
          <button
            className="btn-sm primary"
            disabled={busy || !jhaOk}
            onClick={onIn}
            title={!jhaOk ? "Crew member must submit today's JHA first" : ''}
          >
            {clockIn.isPending ? '…' : (jhaOk ? 'Clock in' : 'JHA needed')}
          </button>
        ) : (
          <>
            <button
              className="btn-sm danger"
              disabled={busy}
              onClick={onOut}
            >
              {clockOut.isPending ? '…' : 'Clock out'}
            </button>
            {stuck && (
              <button
                className="btn-sm"
                disabled={busy}
                onClick={() => setForceCloseOpen(true)}
                title="Shift open >12h — force close it"
              >
                Force close
              </button>
            )}
          </>
        )}
      </div>
      {forceCloseOpen && (
        <ForceCloseModal
          member={member}
          onCancel={() => setForceCloseOpen(false)}
          onConfirm={onForceClose}
          pending={forceClose.isPending}
        />
      )}
    </li>
  );
}

function ForceCloseModal({ member, onCancel, onConfirm, pending }) {
  const [reason, setReason] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const trimmed = reason.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
  };
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <form className="modal-panel" onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h3 className="panel-title">
          <span><i className="ph ph-warning-circle" /> Force-close {member.user_name}'s shift</span>
        </h3>
        <div style={{ fontSize: 12, color: 'var(--warm-gray)', marginBottom: 8 }}>
          They've been clocked in for {formatDuration(Date.now() - member.open_shift.clock_in_at)}.
          A reason is required and is captured in the audit log.
        </div>
        <textarea
          className="field-input"
          rows={3}
          autoFocus
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. forgot to clock out at end of day"
          required
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button type="button" className="btn-sm" onClick={onCancel} disabled={pending}>Cancel</button>
          <button type="submit" className="btn-sm danger" disabled={pending || !reason.trim()}>
            {pending ? 'Closing…' : 'Force close'}
          </button>
        </div>
      </form>
    </div>
  );
}
